import { deepClone, deepEqual, isPlainObject } from './path';
import type { LoanPatchOp } from './types';

const PATCH_IGNORED_ROOT_FIELDS = new Set(['lastModified']);

function isPatchIgnoredPath(path: string): boolean {
    if (!path) {
        return false;
    }
    const rootSegment = path.split('.')[0];
    return PATCH_IGNORED_ROOT_FIELDS.has(rootSegment);
}

function buildLeafPatchOp(params: {
    path: string;
    previousExists: boolean;
    previousValue: unknown;
    nextExists: boolean;
    nextValue: unknown;
}): LoanPatchOp | null {
    const { path, previousExists, previousValue, nextExists, nextValue } = params;
    if (!path) {
        return null;
    }

    if (!nextExists) {
        return {
            op: 'unset',
            path,
            expectedExists: previousExists,
            expectedValue: deepClone(previousValue),
        };
    }

    // JSON payloads cannot carry `undefined` values. If a caller leaves a key
    // present with `undefined`, treat it as field removal semantics.
    if (nextValue === undefined) {
        if (!previousExists) {
            return null;
        }
        return {
            op: 'unset',
            path,
            expectedExists: true,
            expectedValue: deepClone(previousValue),
        };
    }

    return {
        op: 'set',
        path,
        value: deepClone(nextValue),
        expectedExists: previousExists,
        expectedValue: deepClone(previousValue),
    };
}

function appendDiffOps(
    previousValue: unknown,
    nextValue: unknown,
    path: string,
    previousExists: boolean,
    nextExists: boolean,
    output: LoanPatchOp[]
): void {
    if (!previousExists && !nextExists) {
        return;
    }

    if (isPatchIgnoredPath(path)) {
        return;
    }

    if (previousExists && nextExists && deepEqual(previousValue, nextValue)) {
        return;
    }

    if (previousExists && nextExists && isPlainObject(previousValue) && isPlainObject(nextValue)) {
        const keySet = new Set<string>([
            ...Object.keys(previousValue),
            ...Object.keys(nextValue),
        ]);
        for (const key of Array.from(keySet).sort()) {
            const childPath = path ? `${path}.${key}` : key;
            const childPreviousExists = Object.prototype.hasOwnProperty.call(previousValue, key);
            const childNextExists = Object.prototype.hasOwnProperty.call(nextValue, key);
            const childPreviousValue = childPreviousExists ? (previousValue as Record<string, unknown>)[key] : undefined;
            const childNextValue = childNextExists ? (nextValue as Record<string, unknown>)[key] : undefined;
            appendDiffOps(
                childPreviousValue,
                childNextValue,
                childPath,
                childPreviousExists,
                childNextExists,
                output
            );
        }
        return;
    }

    if (previousExists && nextExists && Array.isArray(previousValue) && Array.isArray(nextValue) && deepEqual(previousValue, nextValue)) {
        return;
    }

    if (previousExists && nextExists && Array.isArray(previousValue) && Array.isArray(nextValue)) {
        // Recurse element-by-element only for stable-length arrays so generated conflict
        // paths can be row/field specific (for example repeater item fields).
        if (previousValue.length === nextValue.length) {
            const maxLength = Math.max(previousValue.length, nextValue.length);
            for (let index = 0; index < maxLength; index += 1) {
                const childPath = path ? `${path}.${index}` : String(index);
                const childPreviousExists = Object.prototype.hasOwnProperty.call(previousValue, index);
                const childNextExists = Object.prototype.hasOwnProperty.call(nextValue, index);
                const childPreviousValue = childPreviousExists ? previousValue[index] : undefined;
                const childNextValue = childNextExists ? nextValue[index] : undefined;
                appendDiffOps(
                    childPreviousValue,
                    childNextValue,
                    childPath,
                    childPreviousExists,
                    childNextExists,
                    output
                );
            }
            return;
        }
    }

    const leafOp = buildLeafPatchOp({
        path,
        previousExists,
        previousValue,
        nextExists,
        nextValue,
    });
    if (leafOp) {
        output.push(leafOp);
    }
}

export function buildLoanPatchOps(previousLoanData: unknown, nextLoanData: unknown): LoanPatchOp[] {
    const ops: LoanPatchOp[] = [];
    appendDiffOps(previousLoanData, nextLoanData, '', true, true, ops);
    return ops.sort((a, b) => a.path.localeCompare(b.path) || a.op.localeCompare(b.op));
}
